import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocale } from '../context/LocaleContext';
import SafeIcon from '../common/SafeIcon';
import { CARD_THEMES, THEME_KEYS } from '../utils/themeUtils';

const CATEGORIES = ['life', 'work', 'birthday', 'travel', 'love', 'other'];

export default function CuteModal({ isOpen, onClose, onSave, onDelete, initialData }) {
  const { t } = useLocale();

  // 取得本地時區的 YYYY-MM-DD
  const getLocalDateString = () => {
    const d = new Date();
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().split('T')[0];
  };

  const [formData, setFormData] = useState({
    title: '',
    icon: '✨',
    theme: 'sakura',
    category: 'other',
    targetDateISO: '',
    isPinned: false,
    note: ''
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setIsSaving(false);
      if (initialData) {
        setFormData({
          ...initialData,
          title: t(initialData.title),
          note: initialData.note ? t(initialData.note) : '',
          icon: initialData.icon || '✨',
          theme: initialData.theme || 'sakura',
          category: initialData.category || 'other'
        });
      } else {
        setFormData({
          title: '',
          icon: '✨',
          theme: 'sakura',
          category: 'other',
          targetDateISO: getLocalDateString(),
          isPinned: false,
          note: ''
        });
      }
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, initialData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.targetDateISO || isSaving) return; // 雙重防呆
    setIsSaving(true);
    try {
      await onSave({
        ...formData,
        title: formData.title.trim(),
        note: formData.note.trim()
      });
    } finally {
      setIsSaving(false);
    }
  };

  const emojis = ['✨', '🎂', '✈️', '❤️', '💸', '🏃', '🎓', '🎮', '🏠', '🎉', '🐱', '🐶', '🎵', '🍔', '🏖️'];

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 sm:px-0">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={isSaving ? undefined : onClose}
          />
          
          {/* 修正：將 div 改為 form，讓 HTML5 required 驗證生效 */}
          <motion.form
            onSubmit={handleSubmit}
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            className="bg-[#fffbf0] dark:bg-slate-900 w-full max-w-md rounded-[2.5rem] shadow-2xl border-4 border-white dark:border-slate-800 relative z-10 flex flex-col max-h-[90vh]"
          >
            <div className={`bg-gradient-to-r ${CARD_THEMES[formData.theme].light.bg} dark:${CARD_THEMES[formData.theme].dark.bg} px-6 py-4 flex justify-between items-center text-gray-800 dark:text-white shrink-0 rounded-t-[2.2rem] transition-colors duration-300`}>
              <h2 className="text-xl font-bold">
                {initialData ? t('modal.editTitle') : t('modal.addTitle')}
              </h2>
              <button type="button" disabled={isSaving} onClick={onClose} className="bg-white/50 dark:bg-black/20 hover:bg-white dark:hover:bg-black/40 p-2 rounded-full transition-colors">
                <SafeIcon name="X" className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 sm:p-6 space-y-5 overflow-y-auto hide-scrollbar-on-mobile">
              <div className="flex gap-3">
                <div className="flex-1 bg-white dark:bg-slate-800 border-2 border-gray-100 dark:border-slate-700 rounded-2xl flex items-center px-3 focus-within:border-pink-400 focus-within:ring-4 focus-within:ring-pink-100 dark:focus-within:ring-pink-900 transition-all">
                  <div className="text-2xl mr-2 select-none">{formData.icon}</div>
                  <input
                    required
                    type="text"
                    maxLength={30}
                    disabled={isSaving}
                    placeholder={t('modal.titlePlaceholder')}
                    className="w-full py-3 sm:py-4 bg-transparent outline-none font-bold text-gray-700 dark:text-gray-200 text-lg"
                    value={formData.title}
                    onChange={e => setFormData({ ...formData, title: e.target.value })}
                  />
                </div>
                <button
                  type="button"
                  disabled={isSaving}
                  onClick={() => setFormData({ ...formData, isPinned: !formData.isPinned })}
                  className={`shrink-0 flex flex-col items-center justify-center w-16 rounded-2xl border-2 transition-all ${
                    formData.isPinned 
                      ? 'bg-amber-50 dark:bg-amber-900/30 border-amber-300 text-amber-600 dark:text-amber-400 shadow-inner' 
                      : 'bg-white dark:bg-slate-800 border-gray-100 dark:border-slate-700 text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-700'
                  }`}
                >
                  <span className="text-xl mb-0.5">{formData.isPinned ? '🌟' : '⭐'}</span>
                  <span className="text-[10px] font-bold">{formData.isPinned ? t('modal.pinned') : t('modal.setPinned')}</span>
                </button>
              </div>

              <div>
                <div className="text-xs font-bold text-gray-400 dark:text-gray-500 mb-2 pl-1">{t('modal.categoryLabel')}</div>
                <div className="flex flex-wrap gap-2 px-1">
                  {CATEGORIES.map(cat => (
                    <button
                      key={cat}
                      type="button"
                      disabled={isSaving}
                      onClick={() => setFormData({ ...formData, category: cat })}
                      className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all border ${
                        formData.category === cat
                          ? 'bg-gray-800 dark:bg-gray-100 text-white dark:text-gray-900 border-transparent shadow-sm'
                          : 'bg-white dark:bg-slate-800 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-slate-700 hover:border-gray-300'
                      }`}
                    >
                      {t(`category.${cat}`)}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="text-xs font-bold text-gray-400 dark:text-gray-500 mb-2 pl-1">{t('modal.themeLabel')}</div>
                <div className="flex gap-3 px-1">
                  {THEME_KEYS.map(key => (
                    <button
                      key={key}
                      type="button"
                      disabled={isSaving}
                      onClick={() => setFormData({ ...formData, theme: key })}
                      className={`w-8 h-8 rounded-full shadow-sm transition-all flex items-center justify-center ${CARD_THEMES[key].dotBg} ${
                        formData.theme === key ? 'ring-4 ring-offset-2 ring-gray-300 dark:ring-gray-600 dark:ring-offset-slate-900 scale-110' : 'hover:scale-110'
                      }`}
                      title={t(CARD_THEMES[key].nameKey)}
                    >
                      {formData.theme === key && <SafeIcon name="Check" className="w-4 h-4 text-white drop-shadow-md" />}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="text-xs font-bold text-gray-400 dark:text-gray-500 mb-1.5 pl-1">{t('modal.iconLabel')}</div>
                <div className="flex gap-2 overflow-x-auto hide-scrollbar-on-mobile pb-2 px-1 -mx-1">
                  {emojis.map(emoji => (
                    <button
                      key={emoji}
                      type="button"
                      disabled={isSaving}
                      className={`text-xl shrink-0 w-11 h-11 flex items-center justify-center rounded-xl transition-all ${
                        formData.icon === emoji 
                          ? 'bg-gray-100 dark:bg-slate-700 scale-110 shadow-sm border border-gray-300 dark:border-gray-500' 
                          : 'bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700'
                      }`}
                      onClick={() => setFormData({ ...formData, icon: emoji })}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <div className="text-xs font-bold text-gray-400 dark:text-gray-500 mb-1.5 pl-1">{t('modal.dateLabel')}</div>
                  <input
                    required
                    type="date"
                    disabled={isSaving}
                    className="w-full px-4 py-3 bg-white dark:bg-slate-800 border-2 border-gray-100 dark:border-slate-700 rounded-xl focus:border-pink-400 outline-none transition-all font-medium text-gray-700 dark:text-gray-200 color-scheme-light dark:[color-scheme:dark]"
                    value={formData.targetDateISO}
                    onChange={e => setFormData({ ...formData, targetDateISO: e.target.value })}
                  />
                </div>
                <div>
                  <div className="text-xs font-bold text-gray-400 dark:text-gray-500 mb-1.5 pl-1">{t('modal.noteLabel')}</div>
                  <input
                    type="text"
                    maxLength={100}
                    disabled={isSaving}
                    placeholder={t('modal.notePlaceholder')}
                    className="w-full px-4 py-3 bg-white dark:bg-slate-800 border-2 border-gray-100 dark:border-slate-700 rounded-xl focus:border-pink-400 outline-none transition-all font-medium text-gray-700 dark:text-gray-200"
                    value={formData.note}
                    onChange={e => setFormData({ ...formData, note: e.target.value })}
                  />
                </div>
              </div>
            </div>

            <div className="p-5 bg-white dark:bg-slate-800 border-t border-gray-100 dark:border-slate-700 flex gap-3 shrink-0 rounded-b-[2.2rem]">
              {initialData && onDelete && (
                <button
                  type="button"
                  disabled={isSaving}
                  onClick={onDelete}
                  className="flex-1 py-3.5 bg-gray-100 dark:bg-slate-700 hover:bg-red-100 dark:hover:bg-red-900/40 text-gray-500 dark:text-gray-300 hover:text-red-500 dark:hover:text-red-400 rounded-xl font-bold transition-colors"
                >
                  {t('modal.deleteBtn')}
                </button>
              )}
              {/* 修正：將按鈕改為 type="submit" 觸發原生驗證 */}
              <button
                type="submit"
                disabled={isSaving}
                className="flex-[2] py-3.5 bg-gray-900 dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-100 text-white dark:text-gray-900 rounded-xl font-bold shadow-lg active:scale-95 transition-all"
              >
                {isSaving ? t('common.processing') : t('modal.saveBtn')}
              </button>
            </div>
          </motion.form>
        </div>
      )}
    </AnimatePresence>
  );
}